import type { ThemePreference } from '@words-happen/theme-preference';
import {
  type KeyboardLayout,
  type ReducedMotionPreference,
  SectionLabel,
  WordsHappenButton,
  WordsHappenFormAlert,
  WordsHappenSegmentedControl,
} from '@words-happen/ui-components';
import type React from 'react';
import { type AccountOption, type AccountSearchEntry, accountOptionTarget } from '../account-options';
import styles from './settings-panel.module.scss';

export const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'auto', label: 'Auto' },
] as const satisfies readonly { value: ThemePreference; label: string }[];

export const REDUCED_MOTION_OPTIONS = [
  { value: 'system', label: 'Follow device' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
] as const satisfies readonly { value: ReducedMotionPreference; label: string }[];

export const KEYBOARD_LAYOUT_OPTIONS = [
  { value: 'qwerty', label: 'QWERTY' },
  { value: 'alphabetical', label: 'Alphabetical' },
] as const satisfies readonly { value: KeyboardLayout; label: string }[];

export const SETTINGS_SECTIONS = {
  device: { id: 'settings-device', title: 'Device' },
  gameplay: { id: 'settings-gameplay', title: 'Gameplay' },
  accessibility: { id: 'settings-accessibility', title: 'Accessibility' },
} as const;

/** The Settings tab's options for account search. Each control takes its label from here. */
export const SETTINGS_OPTIONS = {
  theme: {
    id: 'theme',
    tab: 'settings',
    section: SETTINGS_SECTIONS.device.title,
    label: 'Theme',
    synonyms: ['dark mode', 'light mode', 'dark', 'light', 'auto', 'colour', 'color', 'appearance'],
  },
  keyboardLayout: {
    id: 'keyboard-layout',
    tab: 'settings',
    section: SETTINGS_SECTIONS.gameplay.title,
    label: 'Keyboard layout',
    synonyms: ['qwerty', 'alphabetical', 'abc', 'keys', 'letters', 'on-screen keyboard'],
    signInTo: 'change your keyboard layout',
  },
  reducedMotion: {
    id: 'reduced-motion',
    tab: 'settings',
    section: SETTINGS_SECTIONS.accessibility.title,
    label: 'Reduced motion',
    synonyms: ['motion', 'animation', 'animations', 'movement'],
    signInTo: 'turn on reduced motion',
  },
} as const satisfies Record<string, AccountOption>;

/** The Settings tab's options as this player sees them: Theme for everyone, the rest only with an account. */
export const settingsSearchEntries = (account: SettingsPanelProps['account']): AccountSearchEntry[] => [
  { option: SETTINGS_OPTIONS.theme, usable: true },
  { option: SETTINGS_OPTIONS.keyboardLayout, usable: account.kind === 'player' },
  { option: SETTINGS_OPTIONS.reducedMotion, usable: account.kind === 'player' },
];

const REDUCED_MOTION_HINT = "Follow device uses your device's own reduced motion setting";
const KEYBOARD_LAYOUT_HINT = 'The order of the letters on the on-screen keyboard';

/** The Accessibility section for a guest: Reduced motion locked at its default, behind a prompt to create an account. */
export interface SettingsPanelGuestAccount {
  kind: 'guest';
  onCreateAccount: () => void;
  onSignIn: () => void;
}

/** The account settings for a signed-in player. Sign out is on the Profile tab. */
export interface SettingsPanelPlayerAccount {
  kind: 'player';
  /** `loading` until the settings arrive (showing the cached or default values), `load-error` if they couldn't. */
  status: 'loading' | 'ready' | 'load-error';
  onRetryLoad: () => void;
  reducedMotion: ReducedMotionPreference;
  onReducedMotionChange: (value: ReducedMotionPreference) => void;
  /** A save of Reduced motion is in flight. */
  reducedMotionSaving?: boolean;
  /** The last save of Reduced motion failed, and the control went back to the saved value. */
  reducedMotionSaveFailed?: boolean;
  onRetryReducedMotion: () => void;
  keyboardLayout: KeyboardLayout;
  onKeyboardLayoutChange: (value: KeyboardLayout) => void;
  /** A save of Keyboard layout is in flight. */
  keyboardLayoutSaving?: boolean;
  /** The last save of Keyboard layout failed, and the control went back to the saved value. */
  keyboardLayoutSaveFailed?: boolean;
  onRetryKeyboardLayout: () => void;
}

export interface SettingsPanelProps {
  themePreference: ThemePreference;
  onThemePreferenceChange: (preference: ThemePreference) => void;
  account: SettingsPanelGuestAccount | SettingsPanelPlayerAccount;
  className?: string;
}

const GuestAccessibility: React.FC<{ account: SettingsPanelGuestAccount }> = ({ account }) => (
  <>
    <div {...accountOptionTarget(SETTINGS_OPTIONS.reducedMotion)}>
      <WordsHappenSegmentedControl
        label={SETTINGS_OPTIONS.reducedMotion.label}
        options={REDUCED_MOTION_OPTIONS}
        value="system"
        onChange={() => undefined}
        disabled
        hint="Create an account to save your settings across devices"
      />
    </div>
    <div className={styles.actions}>
      <WordsHappenButton variant="primary" onClick={account.onCreateAccount}>
        Create free account
      </WordsHappenButton>
      <p className={styles.signIn}>
        Have an account?{' '}
        <button type="button" className={styles.link} onClick={account.onSignIn}>
          Sign in
        </button>
      </p>
    </div>
  </>
);

interface AccountSettingProps<Value extends string> {
  option: AccountOption;
  hint: string;
  options: readonly { value: Value; label: string }[];
  status: SettingsPanelPlayerAccount['status'];
  value: Value;
  onChange: (value: Value) => void;
  saving?: boolean;
  saveFailed?: boolean;
  onRetry: () => void;
}

/** One account setting: locked and busy while loading, a spinner while saving, and an alert if its save failed. */
const AccountSetting = <Value extends string>({
  option,
  hint,
  options,
  status,
  value,
  onChange,
  saving,
  saveFailed,
  onRetry,
}: AccountSettingProps<Value>) => {
  const loading = status === 'loading';
  const { label } = option;

  return (
    <>
      <div {...accountOptionTarget(option)}>
        <WordsHappenSegmentedControl
          label={label}
          options={options}
          value={value}
          onChange={onChange}
          disabled={status !== 'ready'}
          busy={loading || saving}
          busyLabel={loading ? 'Loading' : 'Saving'}
          hint={hint}
        />
      </div>
      {/* WordsHappenFormAlert is role="alert" (implicitly aria-live), so each error is read out as it appears. */}
      {status === 'ready' && saveFailed ? (
        <div className={styles.error}>
          <WordsHappenFormAlert title={`${label} wasn't saved`}>
            It's back to your saved setting. Check your connection and try again.
          </WordsHappenFormAlert>
          <WordsHappenButton size="sm" onClick={onRetry}>
            Retry
          </WordsHappenButton>
        </div>
      ) : null}
    </>
  );
};

const PlayerAccessibility: React.FC<{ account: SettingsPanelPlayerAccount }> = ({ account }) => (
  <>
    <AccountSetting
      option={SETTINGS_OPTIONS.reducedMotion}
      hint={REDUCED_MOTION_HINT}
      options={REDUCED_MOTION_OPTIONS}
      status={account.status}
      value={account.reducedMotion}
      onChange={account.onReducedMotionChange}
      saving={account.reducedMotionSaving}
      saveFailed={account.reducedMotionSaveFailed}
      onRetry={account.onRetryReducedMotion}
    />
    {account.status === 'load-error' ? (
      <div className={styles.error}>
        <WordsHappenFormAlert title="Couldn't load your settings">
          Check your connection and try again.
        </WordsHappenFormAlert>
        <WordsHappenButton size="sm" onClick={account.onRetryLoad}>
          Try again
        </WordsHappenButton>
      </div>
    ) : null}
  </>
);

/** Gameplay for a guest: Keyboard layout locked at QWERTY, the default. */
const GuestGameplay: React.FC = () => (
  <div {...accountOptionTarget(SETTINGS_OPTIONS.keyboardLayout)}>
    <WordsHappenSegmentedControl
      label={SETTINGS_OPTIONS.keyboardLayout.label}
      options={KEYBOARD_LAYOUT_OPTIONS}
      value="qwerty"
      onChange={() => undefined}
      disabled
      hint="Create an account to choose your keyboard layout"
    />
  </div>
);

/** Gameplay for a signed-in player: Keyboard layout, saved to their account like the Account settings. */
const PlayerGameplay: React.FC<{ account: SettingsPanelPlayerAccount }> = ({ account }) => (
  <AccountSetting
    option={SETTINGS_OPTIONS.keyboardLayout}
    hint={KEYBOARD_LAYOUT_HINT}
    options={KEYBOARD_LAYOUT_OPTIONS}
    status={account.status}
    value={account.keyboardLayout}
    onChange={account.onKeyboardLayoutChange}
    saving={account.keyboardLayoutSaving}
    saveFailed={account.keyboardLayoutSaveFailed}
    onRetry={account.onRetryKeyboardLayout}
  />
);

interface SettingsSectionProps {
  section: (typeof SETTINGS_SECTIONS)[keyof typeof SETTINGS_SECTIONS];
  children: React.ReactNode;
}

/** A titled group of settings. */
const SettingsSection: React.FC<SettingsSectionProps> = ({ section: { id, title }, children }) => (
  <section className={styles.section} aria-labelledby={id}>
    <SectionLabel as="h2">
      <span id={id}>{title}</span>
    </SectionLabel>
    {children}
  </section>
);

/**
 * The Settings tab of the account screen (/settings): a Device section (Theme, saved on this device, for everyone), a
 * Gameplay section (Keyboard layout) and an Accessibility section (Reduced motion), both saved to the player's account.
 * Every control saves as it changes, so there is no Save button. Guests see the account settings locked, with a prompt
 * to create an account.
 */
export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  themePreference,
  onThemePreferenceChange,
  account,
  className,
}) => (
  <div className={[styles.panel, className].filter(Boolean).join(' ')}>
    <SettingsSection section={SETTINGS_SECTIONS.device}>
      <div {...accountOptionTarget(SETTINGS_OPTIONS.theme)}>
        <WordsHappenSegmentedControl
          label={SETTINGS_OPTIONS.theme.label}
          options={THEME_OPTIONS}
          value={themePreference}
          onChange={onThemePreferenceChange}
          hint="Auto matches your device's light or dark setting"
        />
      </div>
    </SettingsSection>

    <SettingsSection section={SETTINGS_SECTIONS.gameplay}>
      {account.kind === 'guest' ? <GuestGameplay /> : <PlayerGameplay account={account} />}
    </SettingsSection>

    <SettingsSection section={SETTINGS_SECTIONS.accessibility}>
      {account.kind === 'guest' ? <GuestAccessibility account={account} /> : <PlayerAccessibility account={account} />}
    </SettingsSection>
  </div>
);
